import { useState, useEffect, useRef } from 'react';

export default function MasonryGrid({ projects, loading, onProjectClick }) {
  const [columns, setColumns] = useState(3);
  const [columnHeights, setColumnHeights] = useState([]);
  const [items, setItems] = useState([]);
  const containerRef = useRef(null);

  // Calculate columns based on viewport width
  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      if (width < 640) setColumns(1);
      else if (width < 1024) setColumns(2);
      else if (width < 1536) setColumns(3);
      else setColumns(4);
    };

    updateColumns();
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, []);

  // Distribute items across columns with animation
  useEffect(() => {
    if (projects.length === 0) {
      setItems([]);
      return;
    }

    const heights = new Array(columns).fill(0);
    const positioned = projects.map((project, index) => {
      const minHeight = Math.min(...heights);
      const columnIndex = heights.indexOf(minHeight);
      const randomHeight = 250 + Math.random() * 200;
      
      const item = {
        ...project,
        column: columnIndex,
        top: heights[columnIndex],
        height: randomHeight
      };

      heights[columnIndex] += randomHeight + 24;
      return item;
    });

    setItems(positioned);
    setColumnHeights(heights);
  }, [projects, columns]);

  if (loading) {
    return (
      <div className="max-w-[1800px] mx-auto px-6" data-testid="masonry-grid-loading">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="skeleton" style={{ height: `${250 + Math.random() * 200}px` }} />
          ))}
        </div>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="max-w-[1800px] mx-auto px-6 text-center py-20" data-testid="no-projects">
        <p className="text-xl text-gray-400">No projects found</p>
      </div>
    );
  }

  const maxHeight = Math.max(...columnHeights, 0);

  return (
    <div className="max-w-[1800px] mx-auto px-6" ref={containerRef} data-testid="masonry-grid">
      <div className="relative" style={{ height: `${maxHeight}px` }}>
        {items.map((project, index) => (
          <ProjectTile
            key={project.id}
            project={project}
            style={{
              position: 'absolute',
              left: `${(project.column / columns) * 100}%`,
              top: `${project.top}px`,
              width: `calc(${100 / columns}% - 18px)`,
              height: `${project.height}px`,
              transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
            onClick={() => onProjectClick(project)}
            delay={index * 50}
          />
        ))}
      </div>
    </div>
  );
}

function ProjectTile({ project, style, onClick, delay }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState({});
  const intervalRef = useRef(null);

  const images = project.images || [];

  // Preload images on hover
  useEffect(() => {
    if (isHovered && images.length > 1) {
      images.forEach((img, idx) => {
        if (idx < 3 && !imageLoaded[idx]) {
          const preloadImg = new Image();
          preloadImg.src = img.full_url;
          preloadImg.onload = () => {
            setImageLoaded(prev => ({ ...prev, [idx]: true }));
          };
        }
      });

      intervalRef.current = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
      }, 1200);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        setCurrentImageIndex(0);
      }
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isHovered, images.length]);

  return (
    <div
      className="fade-in cursor-pointer overflow-hidden rounded-2xl shadow-md hover:shadow-2xl"
      style={{
        ...style,
        animationDelay: `${delay}ms`
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      data-testid={`project-tile-${project.id}`}
    >
      <div className="relative w-full h-full">
        {images.length > 0 ? (
          <>
            {images.map((img, idx) => (
              <img
                key={img.id}
                src={img.thumb_url}
                alt={project.title}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                  idx === currentImageIndex ? 'opacity-100' : 'opacity-0'
                }`}
                loading="lazy"
              />
            ))}
          </>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
            <span className="text-gray-400 text-sm">No image</span>
          </div>
        )}

        {/* Overlay */}
        <div
          className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 hover:opacity-100"
          style={{ transition: 'opacity 0.4s ease' }}
        >
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {project.title}
            </h3>
            <p className="text-sm opacity-90">{project.category}</p>
            {images.length > 1 && (
              <p className="text-xs mt-2 opacity-75">{images.length} images</p>
            )}
            {project.featured && (
              <span className="inline-block mt-2 px-2 py-1 text-xs rounded" style={{ background: '#b8d71b', color: '#1a1a1a' }}>
                Featured
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}